<template>
  <div ref="inputContainer" class="border-b border-gray pt-12 last:border-b-0">
    <div class="flex justify-between items-center mb-12">
      <div class="text-sm font-medium">
        {{ label }}:
      </div>
      <div class="flex items-center">
        <ion-datetime-button v-if="!required" :datetime="id" />
        <template v-else>
          <button
            type="button"
            class="datetime-pill"
            :aria-label="`${label}, datum`"
            @click="datePickerOpen = true"
          >
            {{ dateValue || 'ÅÅÅÅ-MM-DD' }}
          </button>
          <button
            type="button"
            class="datetime-pill"
            :aria-label="`${label}, tid`"
            @click="timePickerOpen = true"
          >
            {{ timeValue || 'TT:MM' }}
          </button>
        </template>
      </div>
    </div>

    <ion-modal v-if="!required" :keep-contents-mounted="true">
      <ion-datetime
        :id="id"
        v-model="selectedDate"
        :show-default-buttons="true"
        :first-day-of-week="1"
        locale="sv-SE"
        done-text="Klar"
        cancel-text="Avbryt"
        :color="darkText ? 'dark' : 'primary'"
      />
    </ion-modal>

    <template v-else>
      <!-- The class is what ion-datetime-button puts on the overlay it opens,
           so these modals get the same fit-content sizing. -->
      <ion-modal
        class="ion-datetime-button-overlay"
        :is-open="datePickerOpen"
        :keep-contents-mounted="true"
        @did-dismiss="datePickerOpen = false"
      >
        <ion-datetime
          :id="`${id}-date`"
          v-model="datePickerValue"
          presentation="date"
          :show-default-buttons="true"
          :first-day-of-week="1"
          locale="sv-SE"
          done-text="Klar"
          cancel-text="Avbryt"
          :color="darkText ? 'dark' : 'primary'"
          @ion-change="datePickerOpen = false"
          @ion-cancel="datePickerOpen = false"
        />
      </ion-modal>

      <ion-modal
        class="ion-datetime-button-overlay"
        :is-open="timePickerOpen"
        :keep-contents-mounted="true"
        @did-dismiss="timePickerOpen = false"
      >
        <ion-datetime
          :id="`${id}-time`"
          v-model="timePickerValue"
          presentation="time"
          :show-default-buttons="true"
          locale="sv-SE"
          done-text="Klar"
          cancel-text="Avbryt"
          :color="darkText ? 'dark' : 'primary'"
          @ion-change="timePickerOpen = false"
          @ion-cancel="timePickerOpen = false"
        />
      </ion-modal>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { IonDatetime, IonDatetimeButton, IonModal } from '@ionic/vue';
import { computed, onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { useUserStore } from '@/store/user';
import { storeToRefs } from 'pinia';

const props = withDefaults(defineProps<{
  id: string;
  label: string;
  defaultDate: string | null;
  subText?: string;
  // When required, the pills start empty and nothing is reported until both
  // the date and the time have been picked.
  required?: boolean;
}>(), {
  required: false,
});

const emits = defineEmits<{
  (e: 'onInput', payload: string | null): void
}>();

const inputContainer = ref();

const userStore = useUserStore();
const { darkText } = storeToRefs(userStore);
const selectedDate = ref(moment().format());

// Initialised eagerly so an existing value renders straight away instead of
// flashing the placeholders first.
const dateValue = ref<string | null>(props.defaultDate ? moment(props.defaultDate).format('YYYY-MM-DD') : null);
const timeValue = ref<string | null>(props.defaultDate ? moment(props.defaultDate).format('HH:mm') : null);
const datePickerOpen = ref(false);
const timePickerOpen = ref(false);

// The pickers work on full ISO strings, the pills on their own part of it.
// With no value at all ion-datetime opens on today's date / the current time.
const datePickerValue = computed({
  get: () => (dateValue.value ? `${dateValue.value}T00:00:00` : null),
  set: (value: string | null) => {
    dateValue.value = value ? moment(value).format('YYYY-MM-DD') : null;
  },
});

const timePickerValue = computed({
  get: () => {
    if (!timeValue.value) {
      return null;
    }
    const datePart = dateValue.value || moment().format('YYYY-MM-DD');
    return `${datePart}T${timeValue.value}:00`;
  },
  set: (value: string | null) => {
    timeValue.value = value ? moment(value).format('HH:mm') : null;
  },
});

const composedDate = computed<string | null>(() => {
  if (!dateValue.value || !timeValue.value) {
    return null;
  }
  return `${dateValue.value}T${timeValue.value}:00`;
});

onMounted(() => {
  if (props.defaultDate) {
    selectedDate.value = props.defaultDate;
  }
});

watch(
  selectedDate,
  () => {
    if (props.required) {
      return;
    }
    emits('onInput', moment(selectedDate.value).format('YYYY-MM-DDTHH:mm:ss'));
  }
);

watch(
  composedDate,
  (value) => {
    if (!props.required) {
      return;
    }
    emits('onInput', value);
  }
);

</script>
<style scoped>
/* Same pill as ion-datetime-button renders, so both modes look alike. */
.datetime-pill {
  margin: 0 2px;
  padding: 6px 12px;
  border: none;
  border-radius: 8px;
  background: var(--ion-color-step-300, var(--ion-background-color-step-300, #edeef0));
  color: var(--ion-text-color, #000);
  font-family: inherit;
  font-size: 1rem;
  transition: 150ms color ease-in-out;
}
</style>
